import { Aide } from '@/components/ui/aide';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';

import api from '../../lib/api';
import { inboundRentalsAPI } from '../../lib/inboundRentalsApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatMois, formatNombre } from '../../lib/formats';
import { libelle, nomLisible, texteSur } from '../../lib/libelles';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { EmptyState } from '../ui/empty-state';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { LoadingState } from '../ui/loading-state';
import { StatsCompactes } from '../ui/stats-compactes';
import { SearchableSelect } from '../ui/searchable-select';
import { SearchableMultiSelect } from '../ui/searchable-multi-select';
import { toast } from '../ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

const aujourdhuiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Une clé qui ne change pas tant que le formulaire reste ouvert: l'envoi est rejouable sans doublon. */
const nouvelleCle = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `avance-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Une observation du compte: un mois « 2026-08 » devient « août 2026 », jamais le code brut. */
export const observationLisible = (note) => {
  if (typeof note === 'string' && /^\d{4}-\d{2}$/.test(note.trim())) return `Fret de ${formatMois(note.trim())}`;
  return texteSur(note, '');
};

const formVide = () => ({
  amount: '',
  account_id: '',
  occurred_at: aujourdhuiIso(),
  note: '',
  truck_ids: [],
  idempotency_key: nouvelleCle(),
});

/** Les camions d'une écriture : plaques figées d'une avance, sinon le camion de l'écriture. */
export const camionsEcriture = (entree, plaques) => {
  const figees = (entree.truck_plates || []).filter(Boolean);
  if (figees.length > 0) return figees.join(', ');
  const choisis = (entree.truck_ids || []).map((id) => plaques.get(id)).filter(Boolean);
  if (choisis.length > 0) return choisis.join(', ');
  return entree.truck_id ? nomLisible(plaques.get(entree.truck_id), '—') : '—';
};

/**
 * Paiements au propriétaire: le compte du transporteur (loyers à payer,
 * avances, règlements) et le formulaire d'une avance. Réservé à la direction.
 */
const InboundPaymentsPanel = ({ contract, onChanged }) => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(formVide);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  // L'argent attend le serveur: boutons inactifs sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;

  const charger = useCallback(async ({ silencieux = false } = {}) => {
    if (!silencieux) setLoading(true);
    setFailed(false);
    try {
      setAccount(await inboundRentalsAPI.account(contract.id));
    } catch (error) {
      setFailed(true);
      toast.error(messageErreur(error, 'Impossible de charger le compte du propriétaire'));
    } finally {
      setLoading(false);
    }
  }, [contract.id]);

  useEffect(() => {
    charger();
  }, [charger]);

  useRafraichirSur(['locations', 'finance', 'depenses'], charger);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/finance/accounts')
      .then((response) => {
        if (!cancelled) setAccounts(response?.data?.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const plaques = useMemo(
    () => new Map((contract.trucks || []).map((ligne) => [ligne.truck_id, ligne.tractor_plate])),
    [contract.trucks],
  );
  // Un camion revenu sur le contrat (deux périodes) n'apparaît qu'une fois.
  const camionOptions = useMemo(() => {
    const vus = new Set();
    return (contract.trucks || [])
      .filter((ligne) => ligne.truck_id && !vus.has(ligne.truck_id) && vus.add(ligne.truck_id))
      .map((ligne) => ({
        value: ligne.truck_id,
        label: nomLisible(ligne.tractor_plate, 'Camion sans plaque'),
        description: ligne.tank_plate ? `Citerne ${ligne.tank_plate}` : undefined,
      }));
  }, [contract.trucks]);
  const accountOptions = useMemo(
    () =>
      accounts.map((compte) => ({
        value: compte.id,
        label: `${compte.name || 'Compte sans nom'}${compte.currency ? ` · ${compte.currency === 'XOF' ? 'FCFA' : compte.currency}` : ''}`,
      })),
    [accounts],
  );

  const setField = (nom, valeur) => setForm((prev) => ({ ...prev, [nom]: valeur }));
  const ouvrir = () => {
    setForm(formVide());
    setSubmitAttempted(false);
    setOpen(true);
  };

  const erreurs = {
    amount: !(Number(form.amount) > 0),
    account_id: !form.account_id,
  };

  const enregistrer = async () => {
    setSubmitAttempted(true);
    if (Object.values(erreurs).some(Boolean)) {
      toast.error('Complétez les champs signalés');
      return;
    }
    setBusy(true);
    try {
      await inboundRentalsAPI.recordAdvance(contract.id, {
        amount: Number(form.amount),
        account_id: form.account_id,
        occurred_at: form.occurred_at || aujourdhuiIso(),
        note: form.note.trim() || null,
        // Facultatif : sans camion choisi, la demande reste celle d'avant.
        ...(form.truck_ids.length > 0 ? { truck_ids: form.truck_ids } : {}),
        idempotency_key: form.idempotency_key,
      });
      toast.success('Avance enregistrée au compte du propriétaire');
      setOpen(false);
      await Promise.all([charger(), onChanged()]);
    } catch (error) {
      toast.error(messageErreur(error, 'Enregistrement de l’avance impossible'));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !account) return <LoadingState shape="table" />;
  if (failed && !account) {
    return (
      <EmptyState
        variant="error"
        title="Le compte du propriétaire n’a pas pu être chargé"
        description="Réessayez dans un instant. Les écritures ne sont pas perdues."
        actionLabel="Réessayer"
        onAction={charger}
      />
    );
  }

  const entrees = account?.entries || [];

  return (
    <div className="space-y-5" data-testid="inbound-payments-panel">
      {/*
        Une seule rangée : l'aide à gauche, les chiffres au milieu, le bouton à droite.
        Sous 1024 px, l'aide et le bouton restent en haut et les chiffres passent dessous ;
        les chiffres vont à la ligne plutôt que de déborder.
      */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="paiements-rangee">
        <Aide titre="Avance" libelle="Qu’est-ce qu’une avance ?" className="order-1">
          Une avance est un paiement au propriétaire avant la validation du relevé : elle vient en déduction de ce que
          vous lui devez.
        </Aide>
        <StatsCompactes
          disposition="libre"
          className="order-3 w-full lg:order-2 lg:w-auto lg:flex-1 lg:justify-center"
          stats={[
            { label: 'Fret à payer', valeur: account?.billed, unite: 'FCFA', aide: 'Relevés validés' },
            { label: 'Déjà réglé', valeur: account?.paid, unite: 'FCFA', aide: 'Avances et paiements' },
            {
              label: 'Reste à payer',
              valeur: account?.balance,
              unite: 'FCFA',
              ton: Number(account?.balance) > 0 ? 'attention' : 'positif',
              testid: 'solde-proprietaire',
            },
          ]}
        />
        <Button onClick={ouvrir} disabled={!enLigne} className="order-2 ml-auto lg:order-3 lg:ml-0" data-testid="new-advance-btn">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Enregistrer une avance
        </Button>
      </div>

      {entrees.length === 0 ? (
        <EmptyState
          illustration="donnee"
          title="Aucune écriture pour ce transporteur"
          description="Le fret apparaît ici à la validation de chaque relevé mensuel, les avances et les règlements dès que vous les enregistrez."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border/70">
          <Table data-testid="compte-proprietaire">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Nature</TableHead>
                <TableHead>Camion</TableHead>
                <TableHead className="text-right">À payer</TableHead>
                <TableHead className="text-right">Réglé</TableHead>
                <TableHead>Observations</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entrees.map((entree) => (
                <TableRow key={entree.id}>
                  <TableCell className="whitespace-nowrap tabular-nums">{formatDate(entree.date)}</TableCell>
                  <TableCell>
                    <Badge variant={entree.direction === 'DU' ? 'warning' : 'success'}>
                      {libelle('nature_ecriture_reglement', entree.kind)}
                    </Badge>
                  </TableCell>
                  <TableCell data-testid={`ecriture-camions-${entree.id}`}>{camionsEcriture(entree, plaques)}</TableCell>
                  <TableCell className={cn('text-right tabular-nums')}>
                    {entree.direction === 'DU' ? formatNombre(entree.amount) : ''}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {entree.direction === 'REGLE' ? formatNombre(entree.amount) : ''}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{observationLisible(entree.note)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Enregistrer une avance au propriétaire</DialogTitle>
            <DialogDescription>
              Le paiement sort du compte de trésorerie choisi et s’inscrit au compte de {contract.lessor_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="avance-montant">Montant</Label>
              <div className="relative">
                <Input
                  id="avance-montant"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={form.amount}
                  onChange={(event) => setField('amount', event.target.value)}
                  className="pr-14 text-right tabular-nums"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  FCFA
                </span>
              </div>
              <FieldError show={submitAttempted && erreurs.amount}>Saisissez un montant supérieur à zéro.</FieldError>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="avance-compte">Payé depuis</Label>
              <SearchableSelect
                id="avance-compte"
                placeholder="Choisir le compte de trésorerie"
                searchPlaceholder="Rechercher un compte…"
                emptyMessage="Aucun compte de trésorerie"
                value={form.account_id}
                onValueChange={(valeur) => setField('account_id', valeur)}
                options={accountOptions}
              />
              <FieldError show={submitAttempted && erreurs.account_id}>Choisissez le compte qui paie.</FieldError>
            </div>
            {camionOptions.length > 0 ? (
              <div className="space-y-1.5">
                <Label htmlFor="avance-camions">Camions</Label>
                <SearchableMultiSelect
                  id="avance-camions"
                  aria-label="Camions de l’avance"
                  placeholder="Facultatif"
                  searchPlaceholder="Rechercher un camion…"
                  emptyMessage="Aucun camion"
                  unite="camion"
                  value={form.truck_ids}
                  onValueChange={(valeurs) => setField('truck_ids', valeurs)}
                  options={camionOptions}
                  data-testid="avance-camions"
                />
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="avance-date">Date du paiement</Label>
                <Input
                  id="avance-date"
                  type="date"
                  value={form.occurred_at}
                  onChange={(event) => setField('occurred_at', event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="avance-note">Observations</Label>
                <Input
                  id="avance-note"
                  value={form.note}
                  placeholder="Facultatif"
                  onChange={(event) => setField('note', event.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button onClick={enregistrer} disabled={busy || !enLigne} data-testid="save-advance-btn">
              {busy ? 'Enregistrement…' : 'Enregistrer l’avance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InboundPaymentsPanel;
